// src/pages/TentativeFamilyPlan/EditTentativeFamilyPlan.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import TentativeFamilyPlanForm from "./TentativeFamilyPlanForm";
import {
    fetchFamilyPlan,
    updateFamilyPlan,
    clearCurrentPlan,
} from "../../../store/slices/tentativeFamilyPlanSlice";

const EditTentativeFamilyPlan = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const { currentPlan, isLoading, error } = useSelector(
        (state) => state.tentativeFamilyPlan
    );

    const [formData, setFormData] = useState(null);
    const [errors, setErrors] = useState({});

    // Fetch plan
    useEffect(() => {
        if (id) dispatch(fetchFamilyPlan(id));
        return () => dispatch(clearCurrentPlan());
    }, [dispatch, id]);

    // Sync currentPlan → formData
    useEffect(() => {
        if (currentPlan) {
            setFormData({
                patientId: patientId || currentPlan.patient_id || "",
                caseId: caseId || currentPlan.case_id || "",
                clientName: currentPlan.client_name || "",
                dateOfAdmission: currentPlan.date_of_admission || "",
                historyTakingMeeting: currentPlan.history_taking_meeting || "",
                firstCallWithFamily: currentPlan.first_call_with_family || "",
                callSchedule: currentPlan.call_schedule || "",
                monthlyFamilyMeeting: currentPlan.monthly_family_meeting || "",
                familyClientMeeting: currentPlan.family_client_meeting || "",
                outings: currentPlan.outings || "",
                homeVisit: currentPlan.home_visit || "",
                homeVisitDate: currentPlan.home_visit_date || "",
                nightStay: currentPlan.night_stay || "",
                nightStayDate: currentPlan.night_stay_date || "",
                weekendStay: currentPlan.weekend_stay || "",
                weekendStayDate: currentPlan.weekend_stay_date || "",
                status: currentPlan.status || "",
            });
        }
    }, [currentPlan]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData?.clientName) newErrors.clientName = "Client name is required.";
        if (!formData?.dateOfAdmission) newErrors.dateOfAdmission = "Admission date is required.";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            Swal.fire("Missing Fields", "Please fill all required fields.", "warning");
            return;
        }

        const payload = {
            patient_id: formData.patientId || patientId,
            case_id: formData.caseId || caseId,
            client_name: formData.clientName,
            date_of_admission: formData.dateOfAdmission,
            history_taking_meeting: formData.historyTakingMeeting,
            first_call_with_family: formData.firstCallWithFamily,
            call_schedule: formData.callSchedule,
            monthly_family_meeting: formData.monthlyFamilyMeeting,
            family_client_meeting: formData.familyClientMeeting,
            outings: formData.outings,
            home_visit: formData.homeVisit,
            home_visit_date: formData.homeVisitDate,
            night_stay: formData.nightStay,
            night_stay_date: formData.nightStayDate,
            weekend_stay: formData.weekendStay,
            weekend_stay_date: formData.weekendStayDate,
            status: formData.status,
        };

        try {
            await dispatch(updateFamilyPlan({ case_id: caseId, data: payload })).unwrap();
            Swal.fire("Updated!", "Family plan updated successfully.", "success").then(() => {
                // Stay on edit page
            });
        } catch (err) {
            const msg = err?.errors
                ? Object.values(err.errors).flat()[0]
                : err.message || "Failed to update plan.";
            Swal.fire("Error", msg, "error");
        }
    };

    if (isLoading || !formData) return <div className="text-center mt-5">Loading...</div>;
    if (error) return <div className="text-center mt-5 text-danger">{error}</div>;

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/phase04/tentative-family-plan/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>

                <div className="card">
                    <div className="card-body">
                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-danger">
                                {Object.values(errors).map((err, i) => (
                                    <div key={i}>{err}</div>
                                ))}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <TentativeFamilyPlanForm
                                formData={formData}
                                handleChange={handleChange}
                            />
                            <div className="col-12 d-flex justify-content-end mt-2">
                                <div className="mt-3 col-md-2 d-flex justify-content-end">
                                    <button type="submit" className="btn btn-primary px-4">
                                        <i className="ri-edit-2-line me-1"></i> Update
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditTentativeFamilyPlan;